// import AppProvider from './providers';
// import AppRouter from './routes';

// export default function App() {
//   return (
//     <AppProvider>
//       <AppRouter />
//     </AppProvider>
//   );
// }
import AppProvider from './providers';
import React from 'react';
import { ConfigProvider, theme as antdTheme, ThemeConfig } from 'antd';
import daDK from 'antd/lib/locale/da_DK';
import deDE from 'antd/lib/locale/de_DE';
import enUS from 'antd/lib/locale/en_US';
import plPL from 'antd/lib/locale/pl_PL';
import GlobalStyle from './styles/GlobalStyle';
import 'typeface-montserrat';
import 'typeface-lato';
import AppRouter from './routes';
import { useLanguage } from './hooks/useLanguage';
import { useAutoNightMode } from '@/hooks/useAutoNightMode';
import { usePWA } from '@/hooks/usePWA';
import { useThemeWatcher } from '@/hooks/useThemeWatcher';
import { useAppSelector } from './hooks/reduxHooks';
import { themeObject } from './styles/themes/themeVariables';
import type { LanguageType } from './interfaces/interfaces';
import { buildCustomTheme } from './styles/themes/custom/customTheme';
import { PanelThemeSynchronizer } from './components/shared/PanelThemeSynchronizer';

const antdLocales: Record<LanguageType, typeof enUS> = {
  pl: plPL,
  en: enUS,
  de: deDE,
  da: daDK
};

const App: React.FC = () => {
  const { language } = useLanguage();
  const {
    theme: currentTheme,
    mode,
    customColors
  } = useAppSelector((state) => state.theme);
  const palette =
    mode === 'custom'
      ? buildCustomTheme(customColors, currentTheme)
      : themeObject[currentTheme];
  const themeConfig: ThemeConfig = {
    algorithm:
      currentTheme === 'dark'
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: palette.primary,
      colorSuccess: palette.success,
      colorError: palette.error,
      colorWarning: palette.warning,
      colorInfo: palette.primary,
      colorBgLayout: palette.layoutBodyBg,
      colorBgContainer: palette.background,
      colorBgElevated: palette.additionalBackground,
      colorText: palette.textMain,
      colorTextSecondary: palette.textLight,
      colorBorder: palette.border,
      colorBorderSecondary: palette.borderBase,
      colorFillAlter: palette.secondaryBackground,
      colorFillSecondary: palette.backgroundColorBase,
      borderRadius: 8,
      fontFamily: 'Montserrat, sans-serif'
    },
    components: {
      Layout: {
        bodyBg: palette.layoutBodyBg,
        headerBg: palette.layoutHeaderBg,
        siderBg: palette.layoutSiderBg
      },
      Menu: {
        itemSelectedColor: palette.textSiderPrimary,
        itemHoverColor: palette.textSiderPrimary,
        itemSelectedBg: palette.itemHoverBg,
        itemHoverBg: palette.itemHoverBg
      },
      Card: {
        colorBgContainer: palette.background
      },
      Table: {
        headerBg: palette.secondaryBackground,
        headerColor: palette.textSecondary,
        headerSplitColor: palette.border,
        borderColor: palette.border,
        rowHoverBg: palette.backgroundColorBase
      },
      Modal: {
        contentBg: palette.background,
        headerBg: palette.background
      },
      Drawer: {
        colorBgElevated: palette.background
      },
      Select: {
        selectorBg: palette.background
      },
      Input: {
        activeBg: palette.background,
        hoverBg: palette.background
      }
    }
  };

  usePWA();

  useAutoNightMode();

  useThemeWatcher();

  return (
    <>
      <meta name="theme-color" content={palette.primary} />
      <GlobalStyle
        $customTheme={mode === 'custom' ? palette : undefined}
        $resolvedTheme={currentTheme}
      />
      <ConfigProvider theme={themeConfig} locale={antdLocales[language]}>
        <AppProvider>
          <PanelThemeSynchronizer />
          <AppRouter />
        </AppProvider>
      </ConfigProvider>
    </>
  );
};

export default App;
